import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, HStack, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, Input, Switch, useToast, Spinner, Center, Badge, Flex, Text, Checkbox, CheckboxGroup, SimpleGrid, Code
} from '@chakra-ui/react';
import { FaEdit, FaTrash, FaPlus, FaPlay, FaHistory } from 'react-icons/fa';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const eventTypes = [
  { value: 'monitor.status_change', label: 'Monitor Status Change', description: 'When a monitor status changes' },
];

const getEventsArray = (eventsStr) => {
  if (!eventsStr || eventsStr === 'all') {
    return eventTypes.map(e => e.value);
  }
  return eventsStr.split(',').filter(e => e);
};

const getEventsString = (eventsArr) => {
  if (eventsArr.length === eventTypes.length) {
    return 'all';
  }
  return eventsArr.join(',');
};

const Webhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [formData, setFormData] = useState({
    name: '', url: '', secret: '', events: 'all', is_active: true, verify_ssl: true,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchWebhooks = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/webhooks/', {
        headers: { 'Authorization': `Token ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data);
      }
    } catch {
      toast({ title: 'Error fetching webhooks', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const handleAdd = () => {
    setSelectedWebhook(null);
    setFormData({ name: '', url: '', secret: '', events: 'all', is_active: true, verify_ssl: true });
    onOpen();
  };

  const handleEdit = (webhook) => {
    setSelectedWebhook(webhook);
    setFormData({
      name: webhook.name, url: webhook.url, secret: '', events: webhook.events, is_active: webhook.is_active, verify_ssl: webhook.verify_ssl,
    });
    onOpen();
  };

  const handleDelete = async (webhook) => {
    if (!window.confirm(`Delete webhook "${webhook.name}"?`)) return;
    try {
      const response = await fetch(`/api/v1/webhooks/${webhook.id}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${localStorage.getItem('token')}`, 'X-CSRFToken': getCookie('csrftoken') },
      });
      if (response.ok) {
        toast({ title: 'Webhook deleted', status: 'success', duration: 3000 });
        fetchWebhooks();
      }
    } catch {
      toast({ title: 'Error deleting webhook', status: 'error', duration: 3000 });
    }
  };

  const handleSave = async () => {
    const url = selectedWebhook ? `/api/v1/webhooks/${selectedWebhook.id}/` : '/api/v1/webhooks/';
    const method = selectedWebhook ? 'PUT' : 'POST';
    const payload = { ...formData };
    if (selectedWebhook && !payload.secret) delete payload.secret;
    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${localStorage.getItem('token')}`, 'X-CSRFToken': getCookie('csrftoken') },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast({ title: selectedWebhook ? 'Webhook updated' : 'Webhook created', status: 'success', duration: 3000 });
        onClose();
        fetchWebhooks();
      } else {
        const error = await response.json();
        toast({ title: 'Error saving webhook', description: JSON.stringify(error), status: 'error', duration: 5000 });
      }
    } catch {
      toast({ title: 'Error saving webhook', status: 'error', duration: 3000 });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  if (loading) {
    return (<Center h="200px"><Spinner size="xl" color="purple.500" thickness="4px" /></Center>);
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">Webhooks</Heading>
          <Text color="gray.500" mt={1}>Configure webhooks to receive notifications when monitor status changes</Text>
        </Box>
        <Button leftIcon={<FaPlus />} bgGradient="linear(to-r, purple.500, purple.600)" color="white" _hover={{ bgGradient: "linear(to-r, purple.600, purple.700)", transform: "translateY(-2px)", boxShadow: "lg" }} onClick={handleAdd} borderRadius="lg" px={6}>Add Webhook</Button>
      </Flex>
      <Box bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.600" py={4}>Name</Th>
              <Th color="gray.600">URL</Th>
              <Th color="gray.600">Events</Th>
              <Th color="gray.600">Status</Th>
              <Th color="gray.600" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {webhooks.length === 0 ? (
              <Tr><Td colSpan={5} textAlign="center" py={8} color="gray.500">No webhooks configured. Click "Add Webhook" to create one.</Td></Tr>
            ) : (
              webhooks.map((webhook) => (
                <Tr key={webhook.id} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                  <Td fontWeight="medium" color="gray.800">{webhook.name}</Td>
                  <Td color="gray.600" maxW="250px" isTruncated>{webhook.url}</Td>
                  <Td>
                    <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                      {webhook.events === 'all' ? 'All Events' : webhook.events.split(',').length + ' events'}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={webhook.is_active ? 'green' : 'gray'} borderRadius="full" px={3} py={1}>
                      {webhook.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      <IconButton aria-label="Edit webhook" icon={<FaEdit />} size="sm" variant="ghost" colorScheme="purple" onClick={() => handleEdit(webhook)} />
                      <IconButton aria-label="Delete webhook" icon={<FaTrash />} size="sm" variant="ghost" colorScheme="red" onClick={() => handleDelete(webhook)} />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>{selectedWebhook ? 'Edit' : 'Add New'} Webhook</ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium">Name</FormLabel>
              <Input name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., Slack Notifications" borderRadius="lg" focusBorderColor="purple.500" />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel fontWeight="medium">URL</FormLabel>
              <Input name="url" value={formData.url} onChange={handleInputChange} placeholder="https://example.com/webhook" borderRadius="lg" focusBorderColor="purple.500" />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Secret {selectedWebhook && <Text as="span" color="gray.500" fontWeight="normal">(leave blank to keep current)</Text>}</FormLabel>
              <Input name="secret" type="password" value={formData.secret} onChange={handleInputChange} placeholder="Optional secret for signing payloads" borderRadius="lg" focusBorderColor="purple.500" />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Events</FormLabel>
              <CheckboxGroup colorScheme="purple" value={getEventsArray(formData.events)} onChange={vals => setFormData(prev => ({ ...prev, events: getEventsString(vals) }))}>
                <SimpleGrid columns={1} spacing={3}>
                  {eventTypes.map((event) => (
                    <Box key={event.value} p={3} borderWidth="1px" borderRadius="lg" borderColor={getEventsArray(formData.events).includes(event.value) ? 'purple.300' : 'gray.200'} bg={getEventsArray(formData.events).includes(event.value) ? 'purple.50' : 'white'} transition="all 0.2s" _hover={{ borderColor: 'purple.300' }}>
                      <Checkbox value={event.value}>
                        <Box ml={2}>
                          <Text fontWeight="medium" fontSize="sm">{event.label}</Text>
                          <Text fontSize="xs" color="gray.500">{event.description}</Text>
                        </Box>
                      </Checkbox>
                    </Box>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>
            <FormControl mt={4} display="flex" alignItems="center">
              <FormLabel htmlFor="is_active" mb="0" fontWeight="medium">Active</FormLabel>
              <Switch id="is_active" name="is_active" isChecked={formData.is_active} onChange={handleInputChange} colorScheme="purple" />
            </FormControl>
            <FormControl mt={4} display="flex" alignItems="center">
              <FormLabel htmlFor="verify_ssl" mb="0" fontWeight="medium">Verify SSL</FormLabel>
              <Switch id="verify_ssl" name="verify_ssl" isChecked={formData.verify_ssl} onChange={handleInputChange} colorScheme="purple" />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} variant="ghost" mr={3}>Cancel</Button>
            <Button colorScheme="purple" onClick={handleSave}>{selectedWebhook ? 'Update' : 'Create'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Webhooks;
